'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';

const ModelViewer = dynamic(
  () => import('./ModelViewer.jsx'),
  { ssr: false }
);

export default function ModelCard({ model, onOpenDetail }) {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="bg-white dark:bg-dark-2 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group">
      {/* 3D Viewer */}
      <div className="aspect-square bg-gray-100 dark:bg-dark-1 relative">
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full mx-auto mb-2" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Lädt...</span>
            </div>
          </div>
        )}

        <ModelViewer
          src={model.file}
          alt={model.title}
          ar={model.arEnabled}
          cameraControls={true}
          autoRotate={model.autoRotate}
          onLoad={() => setIsLoaded(true)}
        />

        {/* AR Badge */}
        {model.arEnabled && (
          <div className="absolute top-3 right-3 bg-accent text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
            AR Ready
          </div>
        )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 pointer-events-none" />
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Category Badge */}
        <span className="inline-block text-xs font-semibold text-accent bg-accent/10 px-3 py-1 rounded-full mb-3">
          {model.category}
        </span>

        {/* Title */}
        <h3 className="text-xl font-heading font-bold mb-2 group-hover:text-accent transition-colors">
          {model.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
          {model.description}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span>{model.fileSize}</span>
          </div>
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <span>{model.polyCount} Polygone</span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {model.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="text-xs bg-gray-100 dark:bg-dark-1 text-gray-700 dark:text-gray-300 px-2 py-1 rounded"
            >
              #{tag}
            </span>
          ))}
          {model.tags.length > 3 && (
            <span className="text-xs text-gray-500 dark:text-gray-500">
              +{model.tags.length - 3}
            </span>
          )}
        </div>

        {/* Action Button */}
        <button
          onClick={() => onOpenDetail(model)}
          className="w-full bg-gray-100 dark:bg-dark-1 hover:bg-accent hover:text-white dark:hover:bg-accent text-gray-900 dark:text-gray-100 font-semibold py-3 rounded-lg transition-all duration-300"
        >
          Details ansehen
        </button>
      </div>
    </div>
  );
}
